import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import {
  ExportParamsType,
  FilesType,
  PickerFilesType,
  PickerType,
} from "pages/pickers/types";
import {
  DetailPickerStateType,
  PickerWrongFileType,
} from "./types/detailPicker";

import { RootState } from "store";
import { PickerFileRequestType } from "pages/detailPicker/types";
import {
  AccountTypesType,
  BankType,
  PickerFileSaveParamsType,
} from "sagas/types/pickers";
import { ProvincesTypes } from "sagas/types/pickers";
import {
  MOTORIZED_VEHICLES_TAGS,
  PICKER_ACCOUNTING_REQUIRED_FILE_TAGS,
  VEHICLE_MOTORIZED_DOCUMENT_TAGS,
  VEHICLE_TAGS,
  VehicleTagType,
} from "utils/constants";
import { FormApi } from "final-form";
import { mergeMotorizedVehicleDocumentSlots } from "pages/detailPicker/vehicleData/mergeMotorizedVehicleFiles";
import type { PickerFileApiRow } from "utils/files";
import {
  doesPickerFileRowIndicateUpload,
  normalizeAllPickerFilesFromApi,
  normalizePickerFileTag,
} from "utils/files";

export const initialState: DetailPickerStateType = {
  dirty: false,
  nameDisplay: "",
  phoneNumber: "",
  invalidBank: true,
  bankNameRequested: false,
  banks: [],
  provinces: [],
  accountTypes: [],
  pendingUserAdminPicker: {
    id: 0,
    enable: false,
    registerDatetime: "",
    status: {
      description: "",
      tag: "",
    },
    region: { id: "", name: "" },
    desiredWorkzones: "",
    personalData: {
      name: "",
      surname: "",
      dateOfBirth: null,
      identification: null,
      email: "",
      phone: {
        areaNumber: "",
        countryNumber: "",
        number: "",
        registerDate: undefined,
      },
      comeFrom: "",
    },
    accountingData: {
      address: {
        id: 1,
        locality: "",
        postalCode: "",
        province: null,
        street: "",
        streetNumber: "",
      },
      companyName: "",
      fiscalNumber: "",
      sapInterlocutor: "",
      id: 0,
      account: {
        number: "",
        bank: {
          tag: "",
          name: "",
        },
        type: "",
      },
    },
    vehicle: {
      type: VEHICLE_TAGS.BICYCLE,
      active: false,
      approve: false,
      patent: "",
      expirationDateDriverLicense: null,
      expirationDateIdentificationVehicle: null,
      expirationDatePolicyVehicle: null,
    },
    ranking: {
      fullRanking: false,
      priority: 0,
    },
    files: {
      personalData: {
        status: "EMPTY",
        content: [],
      },
      accountingData: {
        status: "EMPTY",
        content: [],
      },
      vehicle: {
        status: "EMPTY",
        content: [],
      },
    },
  },
  fiscalNumberDuplicated: false,
  emailOrUsernameDuplicated: false,
  hasSaved: true,
  pickerTermsStatus: null,
  vehicleDocsMotorizedSlotsRequested: false,
  vehicleFileSnapshotByType: {},
  currentFormVehicleType: undefined,
};

const SLICE_NAME = "detailPicker";

const pickVehicleFilesMergeType = (
  state: DetailPickerStateType,
  apiVehicleType: VehicleTagType,
  vehicleFiles: PickerType["files"]["vehicle"]
): VehicleTagType => {
  if (MOTORIZED_VEHICLES_TAGS.includes(apiVehicleType)) {
    return apiVehicleType;
  }
  const hasMotorizedDocTags = vehicleFiles.content.some((f) =>
    (VEHICLE_MOTORIZED_DOCUMENT_TAGS as readonly string[]).includes(f.tag)
  );
  if (hasMotorizedDocTags || state.vehicleDocsMotorizedSlotsRequested) {
    return MOTORIZED_VEHICLES_TAGS[0];
  }
  return apiVehicleType;
};

const applyPickerPayloadWithVehicleMerge = (
  state: DetailPickerStateType,
  payload: PickerType
) => {
  const previousPickerId = state.pendingUserAdminPicker.id;
  state.pendingUserAdminPicker = { ...payload };
  state.pendingUserAdminPicker.files = normalizeAllPickerFilesFromApi(
    state.pendingUserAdminPicker.files
  );

  if (previousPickerId !== payload.id) {
    state.vehicleFileSnapshotByType = {};
    state.currentFormVehicleType = undefined;
  }

  const apiVt = state.pendingUserAdminPicker.vehicle.type;

  const formType = state.currentFormVehicleType;
  if (formType && formType !== apiVt && state.vehicleFileSnapshotByType[formType]) {
    const snap = state.vehicleFileSnapshotByType[formType]!;
    state.pendingUserAdminPicker.files.vehicle = {
      ...snap,
      content: snap.content.map((r) => ({ ...r })),
    };
  } else {
    const mergedVehicleFiles = mergeMotorizedVehicleDocumentSlots(
      pickVehicleFilesMergeType(
        state,
        apiVt,
        state.pendingUserAdminPicker.files.vehicle
      ),
      state.pendingUserAdminPicker.files.vehicle
    );
    state.pendingUserAdminPicker.files.vehicle = mergedVehicleFiles;
  }

  if (MOTORIZED_VEHICLES_TAGS.includes(apiVt)) {
    state.vehicleDocsMotorizedSlotsRequested = false;
  }
};

const getErrorItem = (files: FilesType, tag: PickerFilesType) => {
  let section: keyof FilesType = "vehicle";
  if (tag === "dni-front" || tag === "dni-back" || tag === "user-face")
    section = "personalData";
  if (tag === "cbu-certificate" || tag === "cuit-certificate")
    section = "accountingData";

  const index = files[section].content.findIndex(
    (f) =>
      normalizePickerFileTag(f.tag) === normalizePickerFileTag(tag)
  );
  if (index >= 0)
    return {
      section,
      index,
    };

  return null;
};

export const detailPickerSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetPicker: (state: DetailPickerStateType) => {
      state.pendingUserAdminPicker = initialState.pendingUserAdminPicker;
      state.vehicleDocsMotorizedSlotsRequested = false;
      state.vehicleFileSnapshotByType = {};
      state.currentFormVehicleType = undefined;
    },
    getPendingUserPickerRequest: (
      _state: DetailPickerStateType,
      _action: PayloadAction<number>
    ) => {},
    getPendingUserPickerSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerType>
    ) => {
      const { payload } = action;
      const { personalData } = payload;
      const { phone } = personalData;
      applyPickerPayloadWithVehicleMerge(state, payload);
      state.invalidBank = !!!(payload.accountingData?.account?.bank as BankType)
        ?.name;
      state.nameDisplay = `${personalData.name} ${personalData.surname}`;
      state.phoneNumber =
        phone.areaNumber && phone.number
          ? `${phone.countryNumber}${phone.areaNumber}${phone.number}`
          : undefined;
      state.emailOrUsernameDuplicated = false;
    },
    getPendingUserPickerError: () => {},
    mergeMotorizedVehicleFileSlots: (
      state: DetailPickerStateType,
      action: PayloadAction<{
        vehicleType: VehicleTagType;
        fromType?: VehicleTagType;
        resetExistingSlots?: boolean;
      } | undefined>
    ) => {
      const toType = action.payload?.vehicleType ?? state.pendingUserAdminPicker.vehicle.type;
      const fromType = action.payload?.fromType;

      state.vehicleDocsMotorizedSlotsRequested = MOTORIZED_VEHICLES_TAGS.includes(toType);
      state.currentFormVehicleType = toType;

      if (fromType && MOTORIZED_VEHICLES_TAGS.includes(fromType)) {
        state.vehicleFileSnapshotByType[fromType] = {
          ...state.pendingUserAdminPicker.files.vehicle,
          content: state.pendingUserAdminPicker.files.vehicle.content.map((r) => ({ ...r })),
        };
      }

      const savedSnapshot = state.vehicleFileSnapshotByType[toType];
      if (savedSnapshot && !action.payload?.resetExistingSlots) {
        state.pendingUserAdminPicker.files.vehicle = {
          ...savedSnapshot,
          content: savedSnapshot.content.map((r) => ({ ...r })),
        };
      } else {
        const merged = mergeMotorizedVehicleDocumentSlots(
          toType,
          state.pendingUserAdminPicker.files.vehicle
        );

        if (action.payload?.resetExistingSlots) {
          merged.content = merged.content.map((row) =>
            (VEHICLE_MOTORIZED_DOCUMENT_TAGS as readonly string[]).includes(
              normalizePickerFileTag(row.tag)
            )
              ? { tag: row.tag, isUpload: false, error: "NONE" as const }
              : row
          );
        }

        merged.status = merged.content.every((r) => r.isUpload)
          ? "COMPLETED"
          : merged.content.some((r) => r.isUpload)
          ? "PENDING"
          : "EMPTY";

        state.pendingUserAdminPicker.files.vehicle = merged;

        if (action.payload?.resetExistingSlots) {
          state.vehicleFileSnapshotByType[toType] = {
            ...merged,
            content: merged.content.map((r) => ({ ...r })),
          };
        }
      }
    },
    setDirty: (
      state: DetailPickerStateType,
      action: PayloadAction<boolean>
    ) => {
      state.dirty = action.payload;
    },
    setWrongFile: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerWrongFileType>
    ) => {
      const { tag, error } = action.payload;
      const item = getErrorItem(state.pendingUserAdminPicker.files, tag);
      if (item) {
        const { section, index } = item;
        state.pendingUserAdminPicker.files[section].content[index].error =
          error;
      }
    },
    resetWrongFiles: (state: DetailPickerStateType) => {
      const { files } = state.pendingUserAdminPicker;
      state.pendingUserAdminPicker.files.personalData.content =
        files.personalData.content.map((f) => ({ ...f, error: "NONE" }));
      state.pendingUserAdminPicker.files.accountingData.content =
        files.accountingData.content.map((f) => ({ ...f, error: "NONE" }));
      state.pendingUserAdminPicker.files.vehicle.content =
        files.vehicle.content.map((f) => ({ ...f, error: "NONE" }));
    },
    getPendingUserPickerExportRequest: (
      _state: DetailPickerStateType,
      _action: PayloadAction<ExportParamsType>
    ) => {},
    getPendingUserPickerExportSuccess: () => {},
    getPendingUserPickerExportError: () => {},
    getPendingUserPickerDocumentsEditRequest: (
      _state: DetailPickerStateType,
      _action: PayloadAction<{
        params: PickerType;
        scrollTo: () => void;
        scrollToEmail: () => void;
        formRef: React.MutableRefObject<FormApi<object, object> | null>;
      }>
    ) => {},
    getPendingUserPickerDocumentsEditSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerType>
    ) => {
      const filesFromForm = normalizeAllPickerFilesFromApi(
        state.pendingUserAdminPicker.files
      );
      state.pendingUserAdminPicker = {
        ...action.payload,
        files: filesFromForm,
      };
      const apiVt = state.pendingUserAdminPicker.vehicle.type;
      state.pendingUserAdminPicker.files.vehicle =
        mergeMotorizedVehicleDocumentSlots(
          pickVehicleFilesMergeType(
            state,
            apiVt,
            state.pendingUserAdminPicker.files.vehicle
          ),
          state.pendingUserAdminPicker.files.vehicle
        );
      if (MOTORIZED_VEHICLES_TAGS.includes(apiVt)) {
        state.vehicleDocsMotorizedSlotsRequested = false;
      }
      state.hasSaved = true;
    },
    getPendingUserPickerDocumentsEditError: () => {},
    getAprovePickerRequest: (
      _state: DetailPickerStateType,
      _action: PayloadAction<{
        params: PickerType;
        goBack: Function;
        scrollTo: () => void;
        scrollToEmail: () => void;
      }>
    ) => {},
    getAprovePickerSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerType>
    ) => {
      applyPickerPayloadWithVehicleMerge(state, action.payload);
    },
    getAprovePickerError: () => {},
    getEditPickerRequest: (
      _state: DetailPickerStateType,
      _action: PayloadAction<{
        params: PickerType;
        scrollTo: () => void;
        scrollToEmail: () => void;
        formRef: React.MutableRefObject<FormApi<object, object> | null>;
      }>
    ) => {},
    getEditPickerSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerType>
    ) => {
      applyPickerPayloadWithVehicleMerge(state, action.payload);
      state.hasSaved = true;
    },
    getEditPickerError: () => {},
    getBanksRequest: (
      state: DetailPickerStateType,
      _action: PayloadAction<void>
    ) => {
      state.invalidBank = false;
    },
    getBanksSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<BankType[]>
    ) => {
      state.banks = action.payload;
    },
    getBankNameError: (state: DetailPickerStateType) => {
      state.invalidBank = true;
      state.bankNameRequested = false;
      state.pendingUserAdminPicker.accountingData.account.bank = {
        tag: "",
        name: "",
      };
    },
    getBanksError: (state: DetailPickerStateType) => {
      state.invalidBank = true;
      state.banks = [];
    },
    getBankNameFetch: (
      state: DetailPickerStateType,
      _action: PayloadAction<{ cbuPrefix?: string }>
    ) => {
      state.invalidBank = false;
      state.bankNameRequested = true;
    },
    getBankNameSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<BankType>
    ) => {
      state.pendingUserAdminPicker.accountingData.account.bank = action.payload;
      state.bankNameRequested = false;
    },
    getPickerFileRequest: (
      _state: DetailPickerStateType,
      _action: PayloadAction<PickerFileRequestType>
    ) => {},
    getPickerFileSuccess: () => {},
    getPickerFileError: () => {},
    getPickerFileSaveRequest: (
      _state: DetailPickerStateType,
      _action: PayloadAction<PickerFileSaveParamsType>
    ) => {},
    getPickerFileSaveSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerFilesType>
    ) => {
      const { payload: tag } = action;
      const item = getErrorItem(state.pendingUserAdminPicker.files, tag);
      if (item) {
        const { section, index } = item;
        const row = state.pendingUserAdminPicker.files[section].content[index];
        row.error = "NONE";
        row.isUpload = true;
      }
      const formType = state.currentFormVehicleType;
      if (formType && state.vehicleFileSnapshotByType[formType]) {
        const snap = state.vehicleFileSnapshotByType[formType]!;
        const normalizedTag = normalizePickerFileTag(tag);
        state.vehicleFileSnapshotByType[formType] = {
          ...snap,
          content: snap.content.map((r) =>
            normalizePickerFileTag(r.tag) === normalizedTag
              ? { ...r, isUpload: true, error: "NONE" as const }
              : r
          ),
        };
      }
    },
    getPickerFileSaveError: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerFilesType>
    ) => {
      const { payload: tag } = action;
      const item = getErrorItem(state.pendingUserAdminPicker.files, tag);
      if (item) {
        const { section, index } = item;
        state.pendingUserAdminPicker.files[section].content[index].error =
          "NETWORK";
      }
    },
    getPickerFileDeleteRequest: (
      _state: DetailPickerStateType,
      _action: PayloadAction<PickerFileRequestType>
    ) => {},
    getPickerFileDeleteSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerFilesType>
    ) => {
      const { payload: tag } = action;
      const item = getErrorItem(state.pendingUserAdminPicker.files, tag);
      if (item) {
        const { section, index } = item;
        state.pendingUserAdminPicker.files[section].content[index].error =
          "NONE";
      }
    },
    getPickerFileDeleteError: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerFilesType>
    ) => {
      const { payload: tag } = action;
      const item = getErrorItem(state.pendingUserAdminPicker.files, tag);
      if (item) {
        const { section, index } = item;
        state.pendingUserAdminPicker.files[section].content[index].error =
          "NETWORK";
      }
    },
    getProvincesRequest: () => {},
    getProvincesError: () => {},
    getProvincesSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<ProvincesTypes[]>
    ) => {
      const { payload } = action;
      state.provinces = payload;
    },
    getAccountTypeRequest: () => {},
    getAccountTypeError: () => {},
    getAccountTypeSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<AccountTypesType[]>
    ) => {
      const { payload } = action;
      state.accountTypes = payload;
    },
    resetAccountType: (state) => {
      state.accountTypes = [];
    },
    resetBanks: (state) => {
      state.banks = [];
    },
    resetProvinces: (state) => {
      state.provinces = [];
    },
    setFiscalDuplicated: (state, action: PayloadAction<boolean>) => {
      state.fiscalNumberDuplicated = action.payload;
    },
    setEmailOrUsernameDuplicated: (state, action: PayloadAction<boolean>) => {
      state.emailOrUsernameDuplicated = action.payload;
    },
    setHasSaved: (state, action: PayloadAction<boolean>) => {
      state.hasSaved = action.payload;
    },
    setPickerTermsStatus: (
      state,
      action: PayloadAction<DetailPickerStateType["pickerTermsStatus"]>
    ) => {
      state.pickerTermsStatus = action.payload;
    },
  },
});

export default detailPickerSlice.reducer;

export const detailPickerSelector = (state: RootState) => state.detailPicker;

export const hasPickerWrongFilesSelector = createSelector(
  (state: RootState) => state.detailPicker,
  (picker) => {
    const { files } = picker.pendingUserAdminPicker;
    return !Object.values(files)
      .map((f) => f.content)
      .flat()
      .every((f) => !f.error || f.error === "NONE");
  }
);

const PERSONAL_REQUIRED_FILE_TAGS = [
  "dni-front",
  "dni-back",
  "user-face",
] as const;

const arePersonalRequiredFilesComplete = (
  personalData: PickerType["files"]["personalData"]
): boolean => {
  if (personalData.status === "COMPLETED") return true;
  return PERSONAL_REQUIRED_FILE_TAGS.every((tag) =>
    personalData.content.some(
      (f) =>
        normalizePickerFileTag(f.tag) === normalizePickerFileTag(tag) &&
        doesPickerFileRowIndicateUpload(f as PickerFileApiRow)
    )
  );
};

const areAccountingRequiredFilesComplete = (
  accountingData: PickerType["files"]["accountingData"]
): boolean => {
  if (accountingData.status === "COMPLETED") return true;
  return PICKER_ACCOUNTING_REQUIRED_FILE_TAGS.every((tag) =>
    accountingData.content.some(
      (f) =>
        normalizePickerFileTag(f.tag) === normalizePickerFileTag(tag) &&
        doesPickerFileRowIndicateUpload(f as PickerFileApiRow)
    )
  );
};

export const hasRequiredPickerFilesLoaded = (
  picker: Pick<PickerType, "files">,
  vehicleTypeForRequirement: VehicleTagType
): boolean => {
  const personalData = picker.files.personalData;
  const accountingData = picker.files.accountingData;
  const vehicle = picker.files.vehicle;
  const isAccountingFilesValid =
    areAccountingRequiredFilesComplete(accountingData);

  const personalOk = arePersonalRequiredFilesComplete(personalData);

  if (MOTORIZED_VEHICLES_TAGS.includes(vehicleTypeForRequirement)) {
    const vehicleDocsComplete = VEHICLE_MOTORIZED_DOCUMENT_TAGS.every((tag) =>
      vehicle.content.some(
        (f) =>
          normalizePickerFileTag(f.tag) === normalizePickerFileTag(tag) &&
          doesPickerFileRowIndicateUpload(f as PickerFileApiRow)
      )
    );
    return personalOk && isAccountingFilesValid && vehicleDocsComplete;
  }
  return personalOk && isAccountingFilesValid;
};

export const hasPickerAllRequiredFilesLoadedSelector = createSelector(
  (state: RootState) => state.detailPicker,
  (picker) =>
    hasRequiredPickerFilesLoaded(
      picker.pendingUserAdminPicker,
      picker.pendingUserAdminPicker.vehicle.type
    )
);

export const actions = detailPickerSlice.actions;
